// @todo: Migrate to 'Icon' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Icon} from '@wandb/weave/components/Icon';
import {clsx} from 'clsx';
import React, {useContext} from 'react';

import {type StorageTableQuery} from '../../../../generated/graphql';
import {
  DEFAULT_IS_ASC,
  type File,
  HeaderCellType,
  type Node,
  StorageTableContext,
  StorageTableDispatchContext,
} from './StorageTableContextProvider';

type StorageTableOrganization = NonNullable<StorageTableQuery['organization']>;

export type StorageTableTeam = NonNullable<
  StorageTableOrganization['teams']
>[number];

// All getValueFunctions must return the same type for a given column,
// because they might be compared to each other (e.g. folders and files within
// the same parent folder)
type GetValueFunctions<T> = {
  entity: (entity: StorageTableTeam) => T;
  folder: (folder: Node) => T;
  file: (file: File) => T;
};

export type HeaderCell = {
  columnLabel: React.ReactNode | string;
  classNameOverride?: string;
  // For now, only support string and number values
  getValueFunctions?: GetValueFunctions<string> | GetValueFunctions<number>;
};

export const DEFAULT_COMPARE_FUNCTION = (a: any, b: any) => (a > b ? 1 : -1);

export const HEADER_BACKGROUND = 'bg-moon-50';

export type HeaderCells = {[key in HeaderCellType]?: HeaderCell};

export const DEFAULT_HEADER_CELLS: {[key in HeaderCellType]?: HeaderCell} = {
  [HeaderCellType.NAME]: {
    columnLabel: 'NAME',
    getValueFunctions: {
      entity: (entity: StorageTableTeam) => entity.name,
      folder: (folder: Node) => folder.name ?? '',
      file: (file: File) => file.name.split('/').pop() ?? '',
    },
  },
  [HeaderCellType.STORAGE]: {
    columnLabel: 'STORAGE',
    getValueFunctions: {
      entity: (entity: StorageTableTeam) => entity.storageBytes,
      folder: (folder: Node) => folder.size ?? 0,
      file: (file: File) => file.sizeBytes,
    },
  },
};

type StorageHeaderProps = {
  headerCells: HeaderCells;
};

export const StorageHeader = ({headerCells}: StorageHeaderProps) => {
  const {sortColumn, isAsc} = useContext(StorageTableContext);
  const dispatch = useContext(StorageTableDispatchContext);

  const sort = (column: HeaderCellType) => {
    const {getValueFunctions} = headerCells[column]!;
    if (sortColumn !== column && getValueFunctions != null) {
      dispatch({type: 'setSortColumn', payload: column});
      dispatch({type: 'setIsAsc', payload: DEFAULT_IS_ASC});
    } else {
      dispatch({type: 'setIsAsc', payload: !isAsc});
    }
  };

  return (
    <div className="flex border-y border-moon-250">
      {Object.keys(headerCells).map((key, i) => {
        const column = key as HeaderCellType;
        const {columnLabel, classNameOverride, getValueFunctions} =
          headerCells[column]!;
        return (
          <div
            key={i}
            className={
              classNameOverride ??
              clsx(
                'w-full rounded-none p-8 font-semibold text-moon-500',
                HEADER_BACKGROUND
              )
            }
            onClick={() => sort(column)}
            onKeyDown={({key}) => {
              if (key === 'Enter') {
                sort(column);
              }
            }}>
            <div
              className={clsx(
                'flex items-center',
                getValueFunctions != null ? 'cursor-pointer' : 'cursor-auto'
              )}
              data-test={`${column}-${
                sortColumn === column
                  ? `sorted-${isAsc ? 'ascending' : 'descending'}`
                  : 'unsorted'
              }`}>
              {columnLabel}
              {sortColumn === column && getValueFunctions && (
                <Icon
                  name={isAsc ? 'sort-ascending' : 'sort-descending'}
                  className="h-16 w-16"
                />
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};
